import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Database } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

import { Modal } from '@/components/Modal';
import { IconPicker } from '@/components/modules/IconPicker';

import { FieldRegistry } from '../FieldRegistry';

/** Renderuje ikonę Lucide po nazwie (z fallbackiem na Database). */
function LucideIcon({ name, className }: { name: string; className?: string }) {
  const Icon = (LucideIcons as Record<string, unknown>)[name] as
    | React.FC<{ className?: string }>
    | undefined;
  if (!Icon) return <Database className={className} />;
  return <Icon className={className} />;
}

/**
 * Pole „ikona": przycisk z nazwą wybranej ikony + okno wyboru (`Modal`). Wydzielony komponent
 * (a nie hooki w `renderInput`), żeby okno miało własny stan i działało na wspólnym rdzeniu okien.
 */
function IconInput({ value, onChange }: { value: unknown; onChange: (next: unknown) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selected = (value as string) || 'Star';

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100">
        <LucideIcon name={selected} className="h-5 w-5 text-accent-600" />
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
      >
        {selected}
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="sm"
        title={t('tenant.modules.iconPicker.title')}
        testId="icon-field-picker"
      >
        <IconPicker
          value={selected}
          onChange={(name) => {
            onChange(name);
            setOpen(false);
          }}
        />
      </Modal>
    </div>
  );
}

FieldRegistry.register({
  type: 'icon',
  renderInput: ({ value, onChange }) => <IconInput value={value} onChange={onChange} />,
  renderCell: (_field, value) => {
    if (!value) return '—';
    const name = String(value);
    return (
      <div className="flex items-center gap-2">
        <LucideIcon name={name} className="h-3.5 w-3.5 text-accent-600" />
        <span className="text-xs text-text-secondary">{name}</span>
      </div>
    );
  },
  validate: (value, field) => {
    if (!value) return field.isRequired ? 'Pole wymagane' : null;
    const name = String(value);
    if (!(LucideIcons as Record<string, unknown>)[name]) return 'Nieprawidłowa nazwa ikony';
    return null;
  },
  defaultValue: 'Star',
});
